<template>
    <section class="services-price">
        <div v-if="unexpectedPaymentIssue">
            <alert class="mb-3" type="error">
                Your payment verification with PayPal failed. Please read below message for additional details.
            </alert>
            <alert type="info">
                <p>
                    It seems like your payment was processed successfully, however, an unexpected issue occured while verifying
                    your payment with paypal.
                    <strong>
                        Please do not try to re-pay until you don't contact and share this issue with YaganKar support team.
                    </strong>
                    We do apologize for the inconvenience and will try to sort it out with you as soon as possible.
                </p>
                <p>
                    After manual verification of your payment with PayPal, we will directly subscribe you and then you will be
                    able to move on with your work creation.
                </p>
            </alert>
        </div>
        <div v-else class="mx-auto" style="max-width: 1000px;">
            <h2 class="text-3xl font-thin text-center">Choose your most appropriate package</h2>
            <div v-if="subscriptions.length > 0">
                <div class="my-12">
                    <h3 class="text-2xl font-thin">Subscribed Packages</h3>
                    <div class="mt-6 space-y-4 md:space-y-6">
                        <div
                            v-for="subscription in subscriptions"
                            :key="subscription.id"
                            class="flex flex-wrap items-center justify-between space-y-2 sm:space-y-0 rounded-lg bg-white shadow-lg py-10 px-8 text-gray-800 hover:shadow-xl"
                        >
                            <div>
                                <h2 class="font-bold uppercase text-xl text-gray-900 leading-none mb-2">
                                    {{ subscription.plan.name.toUpperCase() }}
                                </h2>
                                <h3 class="text-gray-500 leading-none">
                                    Job will be visible for {{ subscription.plan.no_of_days }} days
                                </h3>
                            </div>
                            <div class="flex flex-wrap items-center space-x-0 sm:space-x-6 space-y-6 sm:space-y-0">
                                <h3 class="text-gray-500 leading-none">
                                    {{ subscription.posted_projects }} out of {{ subscription.total_projects }} jobs used from
                                    this package
                                </h3>
                                <button class="btn btn-primary" @click="selectPackage(subscription)">
                                    Use this Package
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="plans.length > 0 && subscriptions.length > 0">
                <hr />
            </div>
            <div v-if="plans.length > 0" class="my-12">
                <h3 class="text-2xl font-thin">Available to Buy & Subscribe</h3>
                <div
                    class="mt-6 flex flex-col md:flex-row justify-start space-y-8 md:space-y-0 space-x-0 md:space-x-12 mx-auto w-full"
                >
                    <div v-for="plan in plans" :key="plan.id" class="w-full md:w-1/3 flex flex-col">
                        <!-- NOTE: will replace the (index==0) with the logic if the auth user subscribe to the plane or not -->
                        <div
                            class="rounded-lg bg-white shadow-lg py-10 px-8 text-gray-800 h-full border-transparent border-t-8 hover:border-teal-500 hover:shadow-xl"
                            :class="{
                                'opacity-50 cursor-not-allowed':
                                    plan.id === (freePlanSubscription ? freePlanSubscription.plan_id : null)
                            }"
                        >
                            <h2 class="text-center uppercase text-5xl text-gray-900 leading-none mt-3 mb-4">
                                {{ isFreePlan(plan) ? 'Free' : plan.formatted_price }}
                            </h2>
                            <h3 class="text-center text-gray-500 leading-none mt-3 mb-4">
                                Job visible for {{ plan.no_of_days }} days
                            </h3>
                            <hr class="my-5" />
                            <h4 class="font-bold text-center uppercase text-xl text-gray-900 leading-none mt-3 mb-4">
                                {{ plan.name }}
                            </h4>
                            <p class="text-gray-500 text-center">
                                This plan includes {{ plan.no_of_projects }} jobs. Each job will be displayed as
                                {{ plan.featured ? 'featured' : 'normal' }} for {{ plan.no_of_days }} days.
                            </p>
                            <div class="flex items-center justify-center mt-5">
                                <template v-if="plan.id !== (freePlanSubscription ? freePlanSubscription.plan_id : null)">
                                    <button v-if="isFreePlan(plan)" class="btn btn-line-primary" @click="buyPlan(plan)">
                                        Use this Package
                                    </button>
                                    <span v-else>
                                        <button
                                            v-if="!selectedPlanToBuy || selectedPlanToBuy.id !== plan.id"
                                            href="#"
                                            class="btn btn-line-primary rounded"
                                            @click="buyPlan(plan)"
                                        >
                                            Buy & Use this Package
                                        </button>
                                        <button
                                            v-if="selectedPlanToBuy && selectedPlanToBuy.id === plan.id"
                                            class="btn text-gray-600 border border-gray-400 rounded"
                                            @click="clearPlans()"
                                        >
                                            <span class="fa fa-times"></span> Discard
                                        </button>
                                    </span>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-show="selectedPlanToBuy">
                <div v-show="verifying">
                    <p class="text-xl">Verifying the payment with Paypal... Please wait</p>
                </div>
                <div v-show="!verifying" class="flex">
                    <div v-if="selectedPlanToBuy" class="w-1/2 pr-4 text-xl font-thin">
                        <p class="text-xl py-2">
                            You have selected the
                            <span class="font-bold uppercase">
                                {{ selectedPlanToBuy.name }} ({{ selectedPlanToBuy.formatted_price }})
                            </span>
                            package.
                        </p>
                        <p class="text-lg py-2">
                            This plan includes <span class="font-semibold">{{ selectedPlanToBuy.no_of_projects }} jobs</span>.
                            Each job will be displayed as
                            <span class="font-semibold">{{ selectedPlanToBuy.featured ? 'featured' : 'normal' }}</span> for
                            <span class="font-semibold">{{ selectedPlanToBuy.no_of_days }} days</span>.
                        </p>
                    </div>
                    <div class="w-1/2">
                        <h3 class="text-xl pt-2 font-thin mb-3">Pay now with below options</h3>
                        <div ref="paymentsContainer"></div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    data() {
        return {
            selectedPackage: null,
            selectedPlanToBuy: null,
            paymentOptionsLoaded: false,
            unexpectedPaymentIssue: false,
            paymentOptions: false,
            paypalBtn: null,
            initiatingPaypal: false,
            plans: [],
            verifying: false,
            subscriptions: [],
            freePlanSubscription: null
        };
    },

    watch: {
        selectedPlanToBuy: function() {
            if (this.paypalBtn) {
                this.paypalBtn.close();
            }
        }
    },

    beforeMount() {
        this.loadPaymentOptions();
    },

    mounted() {
        this.fetchPlans();
    },

    methods: {
        fetchPlans() {
            NProgress.inc();

            axios
                .get('/api/plans-subscriptions')
                .then(response => {
                    this.plans = response.data.data.plans;
                    this.subscriptions = response.data.data.subscribed;
                    this.freePlanSubscription = response.data.data.free_plan_subscription;
                })
                .catch(() => flash.error())
                .finally(() => NProgress.done());
        },

        loadPaymentOptions() {
            let paypalSmartBtnScript = document.createElement('script');
            paypalSmartBtnScript.setAttribute(
                'src',
                'https://www.paypal.com/sdk/js?client-id=AeieZrRLQyKQm6VUgXRtzUraENYIa5Gk0pJYD8oweg6jT9K3i8myJbsdTFAaXTKPu8-9TV5lDDy_tlb4'
            );
            document.head.appendChild(paypalSmartBtnScript);
        },

        showPaymentOptions() {
            this.initiatingPaypal = true;
            let amount = this.selectedPlanToBuy.price;
            let packageName = this.selectedPlanToBuy.name;

            this.paypalBtn = paypal.Buttons({
                createOrder: function(data, actions) {
                    return actions.order.create({
                        purchase_units: [
                            {
                                description: `YaganKar's ${packageName.toUpperCase()} Package Subscription`,
                                amount: {
                                    value: amount
                                }
                            }
                        ]
                    });
                },
                onApprove: this.onPayPalApprove,
                onError: this.onPayPalError
            });

            this.paypalBtn.render(this.$refs.paymentsContainer).then(() => (this.initiatingPaypal = false));
        },

        isFreePlan(plan) {
            return plan.price === null || plan.price === 0.0;
        },

        buyPlan(plan) {
            if (this.isFreePlan(plan)) {
                this.subscribeToFreePlan(plan);
                return;
            }

            if (this.initiatingPaypal) {
                alert('Please wait, paypal is loading. Retry in few seconds.');
                return;
            }

            this.selectedPlanToBuy = plan;

            this.$nextTick(() => this.showPaymentOptions());
        },

        subscribeToFreePlan(plan) {
            NProgress.inc();

            axios
                .post(`/api/plans/${plan.id}/verify-subscribe`)
                .then(response => {
                    flash.success({ message: `You have successfully subscribed to ${plan.name.toUpperCase()}. plan` });
                    this.selectPackage(response.data.data);
                })
                .catch(() => flash.error())
                .finally(() => NProgress.done());
        },

        clearPlans() {
            if (this.initiatingPaypal) {
                alert('Please wait, paypal is loading. Retry in few seconds.');
                return;
            }

            this.selectedPlanToBuy = null;
        },

        onPayPalError() {
            alert('Error: Payment process failed due to an unknown problem. Contact admin please.');
        },

        onPayPalApprove(data, actions) {
            NProgress.inc();
            this.verifying = true;

            return actions.order.capture().then(details => {
                if (details.status !== 'COMPLETED') {
                    alert('It seems like the funds capturing failed. Please contact admin.');
                    return;
                }

                axios
                    .post(`/api/plans/${this.selectedPlanToBuy.id}/verify-subscribe?order_id=${details.id}`)
                    .then(response => {
                        flash.success({ message: 'Payment was done and verified.' });
                        this.selectPackage(response.data.data);
                    })
                    .catch(() => {
                        this.unexpectedPaymentIssue = true;
                    })
                    .finally(() => {
                        this.verifying = false;
                        NProgress.done();
                    });
            });
        },

        selectPackage(subscriptionPackage) {
            this.$emit('package-selected', subscriptionPackage);
            this.selectedPackage = subscriptionPackage;
        }
    }
};
</script>
